<template>
  <div class="dashboard-widget-renderer">
    <div
      v-if="hasRenderError"
      class="dashboard-widget-error-state"
      role="alert"
    >
      <div class="dashboard-widget-error-icon" aria-hidden="true">
        <i class="f7-icons">exclamationmark_triangle</i>
      </div>
      <div class="dashboard-widget-error-copy">
        <h2>No se pudo cargar este widget</h2>
        <p v-if="widget.title">{{ widget.title }}</p>
      </div>
    </div>

    <template v-else>
      <LastSamples
        v-if="widget.id === 'last-samples'"
        :available-samples="aggregateInfo"
        :last-refresh-at="lastDashboardRefreshAt"
      />

      <PowerBiAnalyticsWidget
        v-else-if="widget.component === 'PowerBiAnalyticsWidget'"
        :shortcuts="powerBiShortcuts"
        :has-online-samples="hasOnlineSamples"
      />

      <div
        v-else-if="widget.id === 'sample-records'"
        class="dashboard-main-card"
      >
        <OnlineSamplesRecord
          :online-samples-record="onlineSamplesRecord"
          layout-mode="grid"
          :can-register-samples="canRegisterSamples"
          :has-online-samples="hasOnlineSamples"
        />
      </div>

      <div
        v-else-if="widget.id === 'complaints'"
        class="dashboard-complaint-chart-slot"
      >
        <ComplaintRecord
          :auto-load="false"
          chart-only
          :compact-chart-only="usesCompactComplaintChart"
          :can-register-complaint="canRegisterComplaint"
          class="dashboard-complaint-record"
          :class="{
            'dashboard-complaint-record--top-row':
              isTopRowComplaintWidget,
          }"
        />
      </div>
    </template>
  </div>
</template>

<script>
import LastSamples from '@/components/Dashboard/LastSamples/index.vue';
import OnlineSamplesRecord from '@/components/Dashboard/OnlineSamplesRecord/index.vue';
import ComplaintRecord from '@/components/Dashboard/ComplaintRecord/index.vue';
import PowerBiAnalyticsWidget from '@/components/Dashboard/PowerBiAnalyticsWidget/index.vue';

export default {
  name: 'DashboardWidgetRenderer',
  components: {
    LastSamples,
    OnlineSamplesRecord,
    ComplaintRecord,
    PowerBiAnalyticsWidget,
  },
  props: {
    widget: {
      type: Object,
      required: true,
    },
    widgetSize: {
      type: String,
      default: '',
    },
    aggregateInfo: {
      type: Array,
      default: () => [],
    },
    analyticsShortcuts: {
      type: Array,
      default: () => [],
    },
    onlineSamplesRecord: {
      type: Object,
      default: () => ({}),
    },
    hasOnlineSamples: {
      type: Boolean,
      default: false,
    },
    onlineSamplesIconSvg: {
      type: String,
      default: '',
    },
    scienceIconSvg: {
      type: String,
      default: '',
    },
    canRegisterComplaint: {
      type: Boolean,
      default: true,
    },
    canRegisterSamples: {
      type: Boolean,
      default: true,
    },
    lastDashboardRefreshAt: {
      type: [Date, String, Number],
      default: null,
    },
    visibleWidgetsCount: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      hasRenderError: false,
    };
  },
  computed: {
    widgetId() {
      return this.widget.id;
    },
    powerBiShortcuts() {
      if (this.widget.analyticsShortcuts?.length) {
        return this.widget.analyticsShortcuts;
      }

      if (this.widget.analyticsShortcut) {
        return [this.widget.analyticsShortcut];
      }

      return this.analyticsShortcuts.filter(
        (shortcut) => shortcut.routeName === this.widget.routeName
      );
    },
    usesCompactComplaintChart() {
      return (
        this.visibleWidgetsCount >= 3 &&
        this.widgetSize !== 'full' &&
        Number(this.widget.visualOrder || this.widget.order) <= 2
      );
    },
    isTopRowComplaintWidget() {
      return (
        Number(this.widget.visualOrder || this.widget.order) <= 2
      );
    },
  },
  watch: {
    widgetId() {
      this.hasRenderError = false;
    },
  },
  errorCaptured() {
    this.hasRenderError = true;
    return false;
  },
};
</script>

<style scoped lang="scss">
.dashboard-widget-renderer,
.dashboard-widget-renderer > * {
  width: 100%;
  height: 100%;
  min-width: 0;
}

.dashboard-main-card,
.dashboard-complaint-chart-slot {
  min-width: 0;
  display: flex;
  width: 100%;
  height: 100%;
}

.dashboard-complaint-chart-slot {
  box-shadow: 0px 1px 4px 0px rgba(87, 84, 95, 0.30196);
  border-radius: 10px;
}

.dashboard-main-card > *,
.dashboard-complaint-record {
  width: 100%;
}

.dashboard-widget-error-state {
  width: 100%;
  height: 100%;
  min-height: 180px;
  box-sizing: border-box;
  padding: 28px;
  border-radius: 10px;
  background: var(--Basic-white, #fff);
  box-shadow: 0px 1px 4px 0px rgba(87, 84, 95, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  color: var(--Neutral-70, #3e3c44);
}

.dashboard-widget-error-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 208, 51, 0.2);
  color: var(--Warning-60, #c95c04);

  i {
    font-size: 24px;
  }
}

.dashboard-widget-error-copy {
  min-width: 0;

  h2 {
    margin: 0;
    color: var(--Primary-Blue-60, #04509f);
    font-size: 17px;
    font-weight: 500;
    line-height: 1.25;
  }

  p {
    margin: 6px 0 0;
    color: var(--Neutral-60, #6a6872);
    font-size: 14px;
    line-height: 1.4;
  }
}

@media (max-width: 768px) {
  .dashboard-widget-error-state {
    min-height: 150px;
    padding: 20px;
    align-items: flex-start;
    justify-content: flex-start;
  }
}
</style>
